import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Row, Col, Button, Card, Accordion, ListGroup } from 'react-bootstrap';
import * as Icon from 'react-feather';

const CLAVE_UNIDAD_OPTIONS = [
    { value: 'KGM', label: 'KGM - Kilogramo' },
    { value: 'TNE', label: 'TNE - Tonelada metrica' },
    { value: 'LTR', label: 'LTR - Litro' },
    { value: 'H87', label: 'H87 - Pieza' },
    { value: 'E48', label: 'E48 - Unidad de servicio' },
    { value: 'XBX', label: 'XBX - Caja' },
    { value: 'XPK', label: 'XPK - Paquete' },
    { value: 'XKI', label: 'XKI - Kit' },
    { value: 'XUN', label: 'XUN - Unidad' },
    { value: 'MTR', label: 'MTR - Metro' },
    { value: 'GRM', label: 'GRM - Gramo' },
];

const emptyMercancia = {
    bienes_transp: '',
    descripcion: '',
    cantidad: 1,
    clave_unidad: 'KGM',
    peso_en_kg: '',
    valor_mercancia: '',
    moneda: 'MXN',
    material_peligroso: false,
    cve_material_peligroso: '',
    embalaje: '',
    stop_index: 0,
};

// Module-level cache so the catalog is loaded only once across all instances
let satCatalogCache = null;
let satCatalogPromise = null;

function loadSatCatalog() {
    if (satCatalogCache) return Promise.resolve(satCatalogCache);
    if (satCatalogPromise) return satCatalogPromise;
    satCatalogPromise = import('../../../assets/sat_productos.json')
        .then(module => {
            satCatalogCache = module.default || module;
            return satCatalogCache;
        });
    return satCatalogPromise;
}

function SatProductSearch({ value, onSelect, index }) {
    const [query, setQuery] = useState(value || '');
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [catalog, setCatalog] = useState(satCatalogCache);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (!catalog) {
            loadSatCatalog().then(data => setCatalog(data));
        }
    }, [catalog]);

    // Sync external value changes
    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchCatalog = useCallback((term) => {
        if (!catalog || term.length < 2) {
            setResults([]);
            setShowDropdown(false);
            return;
        }
        const lower = term.toLowerCase();
        const matches = [];
        for (let i = 0; i < catalog.length && matches.length < 20; i++) {
            const [clave, desc] = catalog[i];
            if (clave.includes(term) || desc.toLowerCase().includes(lower)) {
                matches.push({ clave, desc });
            }
        }
        setResults(matches);
        setHighlightIndex(-1);
        setShowDropdown(matches.length > 0);
    }, [catalog]);

    const handleChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchCatalog(val), 150);
    };

    const handleSelect = (item) => {
        setQuery(item.clave);
        setShowDropdown(false);
        onSelect(index, item.clave, item.desc);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown || results.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && highlightIndex >= 0) {
            e.preventDefault();
            handleSelect(results[highlightIndex]);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <Form.Control
                value={query}
                onChange={handleChange}
                onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
                onKeyDown={handleKeyDown}
                placeholder="Buscar clave o descripcion..."
                size="sm"
                autoComplete="off"
            />
            {showDropdown && (
                <ListGroup
                    style={{
                        position: 'absolute',
                        zIndex: 1050,
                        width: '100%',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                >
                    {results.map((item, i) => (
                        <ListGroup.Item
                            key={item.clave}
                            action
                            active={i === highlightIndex}
                            onClick={() => handleSelect(item)}
                            style={{ fontSize: '0.85rem', padding: '4px 8px', cursor: 'pointer' }}
                        >
                            <strong>{item.clave}</strong> - {item.desc}
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </div>
    );
}

function MercanciasForm({ mercancias, setMercancias, stops = [] }) {

    const addMercancia = () => {
        setMercancias([...mercancias, { ...emptyMercancia }]);
    };

    const removeMercancia = (index) => {
        setMercancias(mercancias.filter((_, i) => i !== index));
    };

    const updateMercancia = (index, field, value) => {
        const updated = [...mercancias];
        updated[index] = { ...updated[index], [field]: value };
        setMercancias(updated);
    };

    const handleSatProductSelect = (index, clave, descripcion) => {
        const updated = [...mercancias];
        updated[index] = { ...updated[index], bienes_transp: clave, descripcion: descripcion };
        setMercancias(updated);
    };

    const getStopLabel = (stopIdx) => {
        if (stops.length === 0) return '';
        const stop = stops[stopIdx];
        if (!stop) return '';
        const origin = stop.origin_address ? stop.origin_address.split(',')[0] : 'Sin origen';
        const dest = stop.destination_address ? stop.destination_address.split(',')[0] : 'Sin destino';
        return `${origin} → ${dest}`;
    };

    return (
        <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Mercancias</h6>
                <Button size="sm" variant="outline-primary" onClick={addMercancia}>
                    <Icon.Plus size={16} /> Agregar Mercancia
                </Button>
            </Card.Header>
            <Card.Body>
                {mercancias.length === 0 && (
                    <p className="text-muted text-center">
                        No hay mercancias. Agrega al menos una para generar la Carta Porte.
                    </p>
                )}
                <Accordion>
                    {mercancias.map((m, index) => (
                        <Accordion.Item eventKey={index.toString()} key={index}>
                            <Accordion.Header>
                                <div className="d-flex justify-content-between w-100 me-3">
                                    <span>
                                        Mercancia {index + 1}: {m.descripcion || '(sin descripcion)'}
                                        {stops.length > 1 && (
                                            <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                                                (Parada {(m.stop_index || 0) + 1})
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-muted small">
                                        {m.peso_en_kg ? `${m.peso_en_kg} kg` : ''}
                                    </span>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body>
                                {stops.length > 1 && (
                                    <Row className="mb-2">
                                        <Col>
                                            <Form.Group>
                                                <Form.Label>Parada *</Form.Label>
                                                <Form.Select
                                                    value={m.stop_index || 0}
                                                    onChange={(e) => updateMercancia(index, 'stop_index', parseInt(e.target.value))}
                                                    size="sm"
                                                >
                                                    {stops.map((stop, sIdx) => (
                                                        <option key={sIdx} value={sIdx}>
                                                            Parada {sIdx + 1}: {getStopLabel(sIdx)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                                <Row className="mb-2">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Clave Prod. SAT *</Form.Label>
                                            <SatProductSearch
                                                value={m.bienes_transp}
                                                onSelect={handleSatProductSelect}
                                                index={index}
                                            />
                                            <Form.Text className="text-muted">
                                                Busca por clave o descripcion
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={8}>
                                        <Form.Group>
                                            <Form.Label>Descripcion *</Form.Label>
                                            <Form.Control
                                                value={m.descripcion}
                                                onChange={(e) => updateMercancia(index, 'descripcion', e.target.value)}
                                                placeholder="Descripcion de la mercancia"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Cantidad *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0.01"
                                                step="0.01"
                                                value={m.cantidad}
                                                onChange={(e) => updateMercancia(index, 'cantidad', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Unidad *</Form.Label>
                                            <Form.Select
                                                value={m.clave_unidad}
                                                onChange={(e) => updateMercancia(index, 'clave_unidad', e.target.value)}
                                                size="sm"
                                            >
                                                {CLAVE_UNIDAD_OPTIONS.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Peso (kg) *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={m.peso_en_kg}
                                                onChange={(e) => updateMercancia(index, 'peso_en_kg', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Valor ($)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={m.valor_mercancia}
                                                onChange={(e) => updateMercancia(index, 'valor_mercancia', e.target.value)}
                                                placeholder="Opcional"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Material Peligroso"
                                            checked={m.material_peligroso}
                                            onChange={(e) => updateMercancia(index, 'material_peligroso', e.target.checked)}
                                        />
                                    </Col>
                                    {m.material_peligroso && (
                                        <>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Clave Mat. Peligroso</Form.Label>
                                                    <Form.Control
                                                        value={m.cve_material_peligroso}
                                                        onChange={(e) => updateMercancia(index, 'cve_material_peligroso', e.target.value)}
                                                        size="sm"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Embalaje</Form.Label>
                                                    <Form.Control
                                                        value={m.embalaje}
                                                        onChange={(e) => updateMercancia(index, 'embalaje', e.target.value)}
                                                        size="sm"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                                <div className="text-end">
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => removeMercancia(index)}
                                    >
                                        <Icon.Trash2 size={14} /> Eliminar
                                    </Button>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Card.Body>
        </Card>
    );
}

export default MercanciasForm;
